/**
 * Accepting a staff invitation — the only way an admin account is ever finished.
 *
 * `POST /v1/auth/admin/invite/accept {token, password}` → 204. The token arrives in the link a
 * superadmin sent; it is not typed and it is not guessable, so the screen reads it from the URL
 * and says plainly when there is none rather than rendering a form that cannot work.
 *
 * **There is no self-registration path, ever** (arch-08 §3). This screen creates nothing: the
 * account already exists, invited by a superadmin. All it does is set the first password. Two-factor
 * enrolment is still mandatory and still happens at the first sign-in, so this hands off to `/login`
 * rather than pretending to be finished.
 *
 * The password rules are the server's. `lib/password.ts` shows them all, satisfied or not, so
 * nobody has to guess which one they missed — but the server is what enforces them, and a 422 from
 * it is displayed verbatim rather than second-guessed.
 */
import { useState } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { Button, Icon } from '../components/primitives';
import { Input } from '../components/forms';
import { apiFetch, errorMessage } from '../lib/api/client';
import { MIN_PASSWORD_LENGTH, passwordChecks, passwordReady } from '../lib/password';
import type { ShellSearch } from '../app/Shell';

export function AcceptInvite() {
  const navigate = useNavigate();
  const { token, theme } = useSearch({ strict: false }) as ShellSearch & { token?: string };

  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const checks = passwordChecks(password, confirmation);
  const ready = passwordReady(checks);

  async function accept() {
    setBusy(true);
    setError('');
    try {
      await apiFetch('/v1/auth/admin/invite/accept', { method: 'POST', body: { token, password } });
      setDone(true);
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        padding: 'var(--sp-lg)',
        background: 'var(--c-bg)',
      }}
    >
      <div style={{ display: 'grid', gap: 'var(--sp-lg)', width: '100%', maxWidth: 420 }}>
        <div style={{ display: 'grid', gap: 'var(--sp-sm)', justifyItems: 'center', textAlign: 'center' }}>
          <span
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: 26,
              fontWeight: 'var(--fw-bold)',
              color: 'var(--c-ink)',
              letterSpacing: '-0.01em',
            }}
          >
            BookMyPandiit
          </span>
          <span className="kicker" style={{ color: 'var(--c-saffron-text)' }}>
            Admin console
          </span>
        </div>

        <div
          style={{
            display: 'grid',
            gap: 'var(--sp-md)',
            background: 'var(--c-surface)',
            border: '1px solid var(--c-border)',
            borderRadius: 'var(--r-card)',
            boxShadow: 'var(--sh-md)',
            padding: 'var(--sp-xxl)',
          }}
        >
          {!token ? (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-sm)' }}>
                <Icon name="triangle-alert" size={18} style={{ color: 'var(--c-warn-text)' }} />
                <span
                  style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--fs-admin-h2)', color: 'var(--c-ink)' }}
                >
                  This link is incomplete
                </span>
              </div>
              <span style={{ fontSize: 'var(--fs-admin-cell)', color: 'var(--c-ink2)' }}>
                An invitation link carries a token, and this address has none — most often because the link was
                copied without its query string. Ask the superadmin who invited you to send it again. There is no
                self-registration path, so there is nothing to fill in here instead.
              </span>
            </>
          ) : done ? (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-sm)' }}>
                <Icon name="circle-check" size={18} style={{ color: 'var(--c-success-text)' }} />
                <span
                  style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--fs-admin-h2)', color: 'var(--c-ink)' }}
                >
                  Password set
                </span>
              </div>
              <span style={{ fontSize: 'var(--fs-admin-cell)', color: 'var(--c-ink2)' }}>
                You are not finished yet: two-factor is mandatory for every admin, and you will set it up the first
                time you sign in.
              </span>
              <Button size="lg" fullWidth onClick={() => navigate({ to: '/login', search: { theme } as ShellSearch })}>
                Go to sign-in
              </Button>
            </>
          ) : (
            <>
              <span
                style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--fs-admin-h2)', color: 'var(--c-ink)' }}
              >
                Set your password
              </span>
              <span style={{ fontSize: 'var(--fs-admin-cell)', color: 'var(--c-ink2)' }}>
                Your account was created by a superadmin. This sets its password for the first time.
              </span>

              {error && (
                <div
                  role="alert"
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 'var(--sp-sm)',
                    background: 'var(--c-error-bg)',
                    color: 'var(--c-error-text)',
                    border: '1px solid var(--c-error)',
                    borderRadius: 'var(--r-tile)',
                    padding: 'var(--sp-md)',
                    fontSize: 'var(--fs-admin-cell)',
                  }}
                >
                  <Icon name="triangle-alert" size={16} />
                  <span style={{ flex: 1 }}>{error}</span>
                </div>
              )}

              <Input density="admin" type="password" label="New password" value={password} onChange={setPassword} />
              <Input
                density="admin"
                type="password"
                label="Confirm new password"
                value={confirmation}
                onChange={setConfirmation}
                helper={`At least ${MIN_PASSWORD_LENGTH} characters.`}
              />

              <ul style={{ display: 'grid', gap: 4, margin: 0, padding: 0, listStyle: 'none' }}>
                {checks.map((check) => (
                  <li
                    key={check.label}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--sp-sm)',
                      fontSize: 'var(--fs-admin-colhead)',
                      color: check.ok ? 'var(--c-success-text)' : 'var(--c-ink3)',
                    }}
                  >
                    <Icon name={check.ok ? 'circle-check' : 'circle'} size={13} />
                    {check.label}
                  </li>
                ))}
              </ul>

              <Button size="lg" fullWidth disabled={!ready || busy} onClick={accept}>
                {busy ? 'Setting…' : 'Set password'}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
